import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { cn } from '@/shared/utils/cn';

import { isAiProviderError } from '../errors';
import { useTestAiProvider } from '../hooks/useTestAiProvider';

/**
 * Runs a live connection check for one AI Provider and reports the result as a
 * toast (spec FR-008) — independent of any LLM Profile, unlike the removed
 * `LlmProfileTestConnectionButton` this supersedes.
 *
 * `overrides` lets a form check the credentials it is *currently holding*. It
 * used to test only what was stored, while the form enabled the button as soon
 * as a fresh token was typed — so typing new credentials and pressing Test
 * silently reported on the old ones, and a stale token read as a rejection of
 * the new one. The result of an overridden check is deliberately not persisted
 * by the backend, so the toast says so rather than implying the badge moved.
 */
export function AiProviderTestConnectionButton({
  id,
  overrides,
  size = 'sm',
  disabled = false,
  className,
}: {
  id: string;
  /**
   * Unsaved values to check instead of the stored ones. Each omitted field falls
   * back to what is stored; omitting this entirely tests the saved provider.
   */
  overrides?: { base_url?: string; token?: string };
  size?: 'sm' | 'default';
  /** Withheld until the caller considers the connection worth testing — e.g. a form still missing a required field. */
  disabled?: boolean;
  /** Alignment is the caller's call — a form stacks it above other fields, a list row centers it beside a badge. */
  className?: string;
}) {
  const test = useTestAiProvider();

  const unsaved = overrides !== undefined;

  function handleClick() {
    test.mutate(
      { id, overrides },
      {
        onSuccess: (result) => {
          if (result.ok) {
            toast.success(
              unsaved
                ? 'Connection verified — save to keep these settings'
                : 'Connection verified',
            );
          } else {
            toast.error(`Connection test failed: ${result.message}`);
          }
        },
        onError: (error) => {
          toast.error(
            `Connection test failed: ${
              isAiProviderError(error)
                ? error.message
                : 'The test could not run. Please try again.'
            }`,
          );
        },
      },
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      disabled={disabled || test.isPending}
      onClick={handleClick}
      className={cn('self-start', className)}
    >
      {test.isPending ? 'Testing…' : 'Test connection'}
    </Button>
  );
}
